import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  Backdrop,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import CancelIcon from "@mui/icons-material/Cancel";
import { toast } from "react-toast";
import OTP from "../common/otpStep";
import {
  generateOTP,
  orderUpdateV1,
  uploadFilesAPI,
} from "../apiCalls";
import { visionContractId } from "../constants";
import { logEventToAnalyticsPlatform } from "../../../helper/events";
import { EVENTS } from "../../centralOms/events_constant";

export default function UploadBillDialog({
  orderId,
  patientName,
  vendorOrderId,
  billUploadDialogVisible,
  setBillUploadDialogVisibility,
  setNavigateToAll,
}) {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [billsSubmitted, setBillsSubmitted] = useState(false);
  const [bills, setBills] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [otpId, setOtpId] = useState("");
  const [attemptsExceeded, setAttemptsExceeded] = useState(false);

  const onFileUpload = async (event) => {
    const { files } = event.target;

    setUploadedFiles([...uploadedFiles, ...Object.values(files)]);
  };

  const removeFile = async (idx) => {
    const currentFiles = uploadedFiles.filter((x, i) => i !== idx);
    setUploadedFiles(currentFiles);
  };

  const uploadFiles = async () => {
    setIsLoading(true);
    try {
      const guids = await uploadFilesAPI(
        uploadedFiles,
        visionContractId,
        "invoice"
      );
      if (guids.length > 0) {
        setBills(guids);
        await sendOtp();
      } else {
        toast.error("File upload failed");
      }
    } catch (err) {
      toast.error("File upload failed or OTP generation failed");
    } finally {
      setIsLoading(false);
    }
  };

  const sendOtp = async () => {
    try {
      const response = await generateOTP(orderId, 'delivery');
      if (response.success) {
        setOtpId(response.otpId);
        setBillsSubmitted(true);
        setAttemptsExceeded(false);
      } else {
        toast.error("OTP generation failed");
      }
    } catch (err) {
      toast.error("OTP generation failed");
    }
  };

  const handleSubmit = async (otpId) => {
    try {
      const body = {
        orderId: orderId,
        vendorOrderId: vendorOrderId,
        orderStatus: "DELIVERED",
        deliveryMode: "PICKUP",
        timestamp: Date.now(),
        invoiceDetails: {
          invoiceUrl: bills,
        },
        usercancellation: false,
      };
      const response = await orderUpdateV1(body, otpId);
      if (response.success) {
        logEventToAnalyticsPlatform({
          name: EVENTS.PC_LS_ACTION.name,
          payload: {
            tab_name: EVENTS.TAB_NAME_PICKUP,
            filterValue: 'true',
            reference_id: '' + orderId,
            reference_provider_id: '',
            service_name: EVENTS.SERVICE_NAME_VISION,
            action: EVENTS.ACTION.DELIVERED_CLICKED,
          },
          schema: EVENTS.PC_LS_ACTION.schema,
        });
        setBillUploadDialogVisibility(false);
        setNavigateToAll(true);
      } else {
        toast.error("Request update failed");
      }
    } catch (err) {
      toast.error(`Request update failed - ${err?.response?.data?.error}`);
    }
  };

  return (
    <Dialog
      open={billUploadDialogVisible}
      onClose={() => setBillUploadDialogVisibility(false)}
      fullWidth={true}
      maxWidth={"sm"}
    >
      <DialogTitle style={{ background: "rgb(245 245 245)" }}>
        <div className="flex justify-between">
          <div className="font-semibold">
            <span className="cursor-pointer">Upload Bill</span>
            <div className="text-xs text-gray-500">
              Order ID: <span className="font-semibold">{orderId} </span>
            </div>
            <div className="text-xs text-gray-500">
              Patient Name:{" "}
              <span className="font-semibold">{patientName} </span>
            </div>
          </div>
          <div
            className="text-gray-500"
            onClick={() => setBillUploadDialogVisibility(false)}
          >
            <span className="cursor-pointer">
              <CloseIcon></CloseIcon>
            </span>
          </div>
        </div>
      </DialogTitle>
      <DialogContent>
        {isLoading && (
          <Backdrop sx={{ color: "#fff", zIndex: 99999 }} open={isLoading}>
            <CircularProgress />
          </Backdrop>
        )}
        {!billsSubmitted || attemptsExceeded ? (
          <Box className="mt-5">
            <div className="m-aut0 text-center border-dotted border-2 border-gray-400 rounded p-5">
              <label htmlFor="rx">
                <div>
                  <FileUploadIcon></FileUploadIcon>
                </div>
                <div className="text-gray-500 text-xs font-semibold cursor-pointer">
                  Upload Bills
                </div>
              </label>
            </div>
            <div className="my-2 d-flex justify-content-center flex-column align-items-center">
              {uploadedFiles.map((fileDetails, idx) => (
                <div
                  key={idx}
                  className="my-2 py-1 px-2"
                  style={{
                    backgroundColor: "#F6FFDF",
                    width: "fit-content",
                    position: "relative",
                  }}
                >
                  {fileDetails.name}
                  <div
                    style={{ position: "absolute", top: "-7px", right: "-7px" }}
                    onClick={() => removeFile(idx)}
                  >
                    <CancelIcon
                      className="text-red-600 mx-2 cursor-pointer"
                      style={{ fontSize: "15px" }}
                    ></CancelIcon>
                  </div>
                </div>
              ))}
            </div>
            <input
              type="file"
              id="rx"
              className="hidden"
              multiple
              name="rx"
              accept=".pdf,image/*"
              onChange={(event) => onFileUpload(event)}
            />

            <div className="pb-5 mt-5 flex justify-between text-center w-full">
              <div
                className="border border-gray-600 rounded-md text-gray-900 p-1 text-sm cursor-pointer font-semibold w-60"
                onClick={() => setBillUploadDialogVisibility(false)}
              >
                <span>Cancel</span>
              </div>
              <button
                className={
                  uploadedFiles?.length === 0
                    ? "border-gray-500 bg-gray-500 text-gray-100 border cursor-default rounded-md w-60 p-1 text-sm font-semibold"
                    : "border-blue-600 bg-blue-600 text-gray-100 border cursor-pointer rounded-md w-60 p-1 text-sm font-semibold"
                }
                disabled={uploadedFiles?.length === 0}
                onClick={() => attemptsExceeded ? sendOtp() : uploadFiles()}
              >
                <span>Submit and Send OTP</span>
              </button>
            </div>
          </Box>
        ) : (
          <OTP handleSubmit={handleSubmit} otpId={otpId} setAttemptsExceeded={setAttemptsExceeded} setIsLoading={setIsLoading}></OTP>
        )}
      </DialogContent>
    </Dialog>
  );
}
